import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Alert, AuthCard } from '@sentinel/ui';
import { useUser } from '@sentinel/auth-core';
import { ConsentCard } from '../features/consent/components/consent-card';
import { useConsentInfo, type ConsentInfoError } from '../features/consent/use-consent-info';
import {
  useAllowConsent,
  useDenyConsent,
  type DecideError,
} from '../features/consent/use-decide';

const search = z.object({
  authorize_request_id: z.string().min(1).optional(),
});

function statusOf(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function ConsentPage() {
  const { t } = useTranslation('consent');
  const { authorize_request_id } = useSearch({ from: '/oauth/consent' });
  const user = useUser();

  const info = useConsentInfo(authorize_request_id);
  const allow = useAllowConsent();
  const deny = useDenyConsent();

  const [topError, setTopError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null);

  if (!authorize_request_id) {
    return (
      <AuthCard>
        <Alert type="error" role="alert">
          {t('missing_request')}
        </Alert>
      </AuthCard>
    );
  }

  // 401 from /auth/consent-info means we lost the session — bounce to /login
  // and bring the user back here after re-auth. Done lazily on render to keep
  // the route module side-effect-free.
  if (info.error && statusOf(info.error) === 401) {
    if (typeof window !== 'undefined') {
      const here = `/oauth/consent?authorize_request_id=${encodeURIComponent(authorize_request_id)}`;
      window.location.assign(`/login?return_to=${encodeURIComponent(here)}`);
    }
    return (
      <AuthCard>
        <p>{t('loading')}</p>
      </AuthCard>
    );
  }

  if (info.isLoading || !info.data) {
    if (info.error) {
      const status = statusOf(info.error as ConsentInfoError);
      const message = status === 400 || status === 404 ? t('expired') : t('generic_error');
      return (
        <AuthCard>
          <Alert type="error" role="alert">
            {message}
          </Alert>
        </AuthCard>
      );
    }
    return (
      <AuthCard>
        <p>{t('loading')}</p>
      </AuthCard>
    );
  }

  async function onAllow() {
    if (!info.data) return;
    setTopError(null);
    setSubmitting('allow');
    try {
      const result = await allow.mutateAsync({
        client_id: info.data.client_id,
        scopes: info.data.requested_scopes,
        authorize_request_id: authorize_request_id!,
        ...(info.data.document_fingerprint && {
          document_fingerprint: info.data.document_fingerprint,
        }),
      });
      window.location.assign(result.redirect_uri);
    } catch (e) {
      setSubmitting(null);
      const status = statusOf(e as DecideError);
      if (status === 401) {
        const here = `/oauth/consent?authorize_request_id=${encodeURIComponent(authorize_request_id!)}`;
        window.location.assign(`/login?return_to=${encodeURIComponent(here)}`);
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  async function onDeny() {
    setTopError(null);
    setSubmitting('deny');
    try {
      const result = await deny.mutateAsync({ authorize_request_id: authorize_request_id! });
      window.location.assign(result.redirect_uri);
    } catch (e) {
      setSubmitting(null);
      const status = statusOf(e as DecideError);
      if (status === 401) {
        const here = `/oauth/consent?authorize_request_id=${encodeURIComponent(authorize_request_id!)}`;
        window.location.assign(`/login?return_to=${encodeURIComponent(here)}`);
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  return (
    <AuthCard width="wide">
      {topError ? (
        <Alert type="error" role="alert">
          {topError}
        </Alert>
      ) : null}
      <ConsentCard
        info={info.data}
        userEmail={user?.email ?? null}
        submitting={submitting}
        onAllow={onAllow}
        onDeny={onDeny}
      />
    </AuthCard>
  );
}

export const Route = createFileRoute('/oauth/consent')({
  validateSearch: search,
  component: ConsentPage,
});
